import React from "react";
import axios from "axios";
import Pagination from './Pagination';
import ReportsError from './ReportsError';
import ReportsTotals from './ReportsTotals';
import { getRefreshToken } from './Utilities';

class TableBody extends React.Component {
  constructor(props) {
    super(props);
    const endpoint = "members/login";
    const { limit } = this.props;

    this.state = {
      membersCache: {},
      currentPage: 0,
      isLoading: false,
      limit: limit || 10,
      url: `react/process?endpoint=${endpoint}&hourly_cutoff=0&turnoffdebug=1&sort=DESC&limit=${limit || 10}`,
      nextUrl: null,
      prevUrl: null,
      fixedHeight: 500, // Initial height before any data is loaded
      error: null, // capture any error messages from API
    };

    this.tableBodyRef = React.createRef();
  }

  componentDidMount() {
    this.loadPage(this.props.next);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.next !== this.props.next ||
      prevProps.limit !== this.props.limit
    ) {
      this.loadPage(this.props.next, this.props.useNextPrevUrl);
    }
  }

  // Load the page from cache if available, otherwise fetch new data
  loadPage(page, useNextPrevUrl = false) {
    const { membersCache } = this.state;

    if (membersCache[page]) {
      // Even if page is cached, ensure next/prev URLs are still valid for the cached page
      const cachedPage = membersCache[page];
      this.setState({
        currentPage: page,
        nextUrl: cachedPage.paging?.next || null,
        prevUrl: cachedPage.paging?.previous || null,
      });
      return;
    }

    // Fetch new data if the page is not cached
    this.fetchData(page, useNextPrevUrl);
  }

  // Fetch data from the API and update state
  fetchData(page = null, useNextPrevUrl = false) {
    let url = this.state.url;

    if (useNextPrevUrl) {
      url = page > this.state.currentPage ? this.state.nextUrl : this.state.prevUrl;
    } else if (page !== null && page !== 0) {
      const offset = (page - 1) * this.state.limit;
      url += `&next=${offset}`;
    }

    this.setState({ isLoading: true });
    this.props.setLoading(true);

    axios
      .get(url)
      .then(response => {
        const error = response.data?.error || null;

        if (error) {
          return this.setState({
            error: error?.message,
            isLoading: false,
          });
        } else {
          const { items, paging } = response.data;
          const members = this.processMembers(items);

          this.setState(prevState => ({
            membersCache: {
              ...prevState.membersCache,
              [page]: { members, paging }, // Cache members and paging info
            },
            currentPage: page,
            nextUrl: paging?.next || null,
            prevUrl: paging?.previous || null,
            isLoading: false,
          }));

          this.props.setLoading(false);
          this.props.setPaging({ next: paging?.next, previous: paging?.previous });
        }
      })
      .catch(() => {
        this.setState({ isLoading: false });
        this.props.setLoading(false);
      });
  }

  // Process and augment the members data
  processMembers(data) {
    const members = [];
    if (data) {
      data.forEach(member => {
        if (member.Services) {
          member.Services.map((service, key) => {
            if (key < 3) {
              const label = service.Service.toLowerCase();
              service.Label = label.replace("authenticator", "");
              return service;
            }
          });
        }
        members.push(member);
      });
    }
    return members;
  }

  render() {
    const { membersCache, currentPage, isLoading, fixedHeight } = this.state;
    const cachedPage = membersCache[currentPage] || {};
    const members = cachedPage.members || [];

    const tableBodyStyle = {
      height: `${fixedHeight}px`,
      display: 'table-row-group',
    };

    if (isLoading) {
      return (
        <tbody style={tableBodyStyle}>
          <tr>
            <td colSpan="7" style={{ textAlign: 'center', padding: '50px 0' }}>
              <div className="react-loading big"></div>
            </td>
          </tr>
        </tbody>
      );
    }

    if (members.length > 0) {
      return (
        <tbody style={tableBodyStyle} ref={this.tableBodyRef}>
          {members.map(member => {
            const services = member.Services || [];
            // Use Username if FirstName or Surname is missing
            const displayName = member.FirstName && member.Surname
              ? `${member.FirstName} ${member.Surname}`
              : member.Username;
            return (
              <tr key={member.ID}>
                <td><i className="fas fa-user-astronaut me-1"></i></td>
                <td>
                  <h5 className="font-14 mt-0 mb-1 fw-normal">{displayName}</h5>
                </td>
                <td>{member.Joined}</td>
                <td>{member.LastVisited}</td>
                <td>{member.Email}</td>
                <td>{member.GeoCountry}</td>
                <td>
                  {services.map(service => (
                    <i key={service.ID} className={`fab me-1 fa-${service.Label}`}></i>
                  ))}
                </td>
              </tr>
            );
          })}
        </tbody>
      );
    } else {
      return <ReportsError error={this.state.error} />;
    }
  }
}

class ReportsLoginMembers extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      next: 0,
      useNextPrevUrl: false,
      total: null,
      refresh: getRefreshToken(),
      limit: 10,
      isLoading: false,
      paging: {}
    };
  }

  // Callback to update the total count from ReportsTotals
  setTotalCount(total) {
    if (total) {
      total = total.toString().replace("~", "").replace(",", "");
      this.setState({ total: parseInt(total, 10) });
    }
  }

  // Handler for pagination changes
  paginationHandler = (next, useNextPrevUrl = false) => {
    this.setState({
      next,
      useNextPrevUrl
    });
  };

  setLoading = (isLoading) => {
    this.setState({ isLoading });
  };

  setPaging = (paging) => {
    this.setState({ paging });
  };

  render() {
    const { limit, isLoading, paging } = this.state;

    return (
      <div className="feed-memberlist">
        <table id="basic-datatable" className="table dt-responsive nowrap w-100">
          <thead>
            <tr>
              <th>Member</th>
              <th>Name</th>
              <th>Joined</th>
              <th>Last Visit</th>
              <th>Email</th>
              <th>Country</th>
              <th>Channels</th>
            </tr>
          </thead>
          <TableBody
            next={this.state.next}
            useNextPrevUrl={this.state.useNextPrevUrl}
            limit={limit}
            type={this.props.type}
            refresh={this.state.refresh}
            setLoading={this.setLoading}
            setPaging={this.setPaging}
            key={this.state.refresh}
          />
        </table>
        <div className="table-controls">
          <ReportsTotals
            next={this.state.next}
            limit={limit}
            endpoint="members/all"
            sendToTable={this.setTotalCount.bind(this)}
          />
          <Pagination
            sendToPage={this.paginationHandler.bind(this)}
            count={this.state.total}
            limit={limit}
            key={this.state.total}
            isLoading={isLoading}
            paging={paging}
          />
        </div>
      </div>
    );
  }
}

export default ReportsLoginMembers;
